import { Capacitor } from '@capacitor/core';
import { Haptics } from '@capacitor/haptics';

/**
 * Loud, attention-grabbing alarm used for danger-zone alerts while the app is in
 * the foreground. The tone is synthesized with the Web Audio API so it is always
 * bundled (no audio asset needed) and plays at full media volume. A repeating
 * vibration is fired in parallel via the Haptics plugin.
 *
 * Call startAlarm() when the alert appears and stopAlarm() when it is dismissed.
 */

type AlarmWindow = typeof globalThis & {
  AudioContext?: typeof AudioContext;
  webkitAudioContext?: typeof AudioContext;
};

let audioContext: AudioContext | null = null;
let oscillator: OscillatorNode | null = null;
let gainNode: GainNode | null = null;
let sirenIntervalId: ReturnType<typeof setInterval> | null = null;
let vibrateIntervalId: ReturnType<typeof setInterval> | null = null;
let isPlaying = false;

// Two-tone siren frequencies (Hz) — high/piercing to cut through ambient noise.
const TONE_HIGH = 880;
const TONE_LOW = 620;
const SIREN_SWITCH_MS = 500;
const VIBRATE_INTERVAL_MS = 1000;
const VIBRATE_DURATION_MS = 600;
const ALARM_VOLUME = 1.0; // 0..1, relative to device media volume

function getAudioContext(): AudioContext | null {
  const win = window as AlarmWindow;
  const Ctor = win.AudioContext || win.webkitAudioContext;
  if (!Ctor) {
    return null;
  }
  if (!audioContext) {
    audioContext = new Ctor();
  }
  return audioContext;
}

/**
 * iOS/WKWebView keeps the AudioContext suspended until a user gesture. The
 * danger-zone alarm fires from a geolocation callback (no gesture), so we prime
 * the context on the first user interaction to unlock later programmatic sound.
 * Call once on app startup.
 */
export function initAlarmAudioUnlock() {
  const unlock = () => {
    const ctx = getAudioContext();
    if (!ctx) {
      return;
    }
    void ctx.resume().catch(() => {
      /* ignore */
    });
    // Play a near-silent blip to fully unlock the audio pipeline on iOS.
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.05);
    }
    catch {
      /* ignore */
    }
    window.removeEventListener('touchstart', unlock);
    window.removeEventListener('click', unlock);
  };

  window.addEventListener('touchstart', unlock, { once: true });
  window.addEventListener('click', unlock, { once: true });
}

function startVibration() {
  if (Capacitor.getPlatform() === 'web') {
    return;
  }
  const buzz = () => {
    void Haptics.vibrate({ duration: VIBRATE_DURATION_MS }).catch(() => {
      /* haptics unavailable — ignore */
    });
  };
  buzz();
  vibrateIntervalId = setInterval(buzz, VIBRATE_INTERVAL_MS);
}

function stopVibration() {
  if (vibrateIntervalId !== null) {
    clearInterval(vibrateIntervalId);
    vibrateIntervalId = null;
  }
}

/** Start the looping alarm. Safe to call repeatedly (no-op if already playing). */
export function startAlarm() {
  if (isPlaying) {
    return;
  }
  isPlaying = true;

  startVibration();

  const ctx = getAudioContext();
  if (!ctx) {
    return;
  }

  // Some platforms suspend the context until a user gesture; try to resume.
  void ctx.resume().catch(() => {
    /* ignore */
  });

  oscillator = ctx.createOscillator();
  gainNode = ctx.createGain();

  oscillator.type = 'square'; // richer harmonics -> more piercing than sine
  oscillator.frequency.setValueAtTime(TONE_HIGH, ctx.currentTime);
  gainNode.gain.setValueAtTime(ALARM_VOLUME, ctx.currentTime);

  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);
  oscillator.start();

  let high = true;
  sirenIntervalId = setInterval(() => {
    if (!oscillator || !audioContext) {
      return;
    }
    high = !high;
    oscillator.frequency.setValueAtTime(
      high ? TONE_HIGH : TONE_LOW,
      audioContext.currentTime,
    );
  }, SIREN_SWITCH_MS);
}

/** Stop the alarm and vibration. Safe to call even if not playing. */
export function stopAlarm() {
  isPlaying = false;

  stopVibration();

  if (sirenIntervalId !== null) {
    clearInterval(sirenIntervalId);
    sirenIntervalId = null;
  }

  if (oscillator) {
    try {
      oscillator.stop();
      oscillator.disconnect();
    }
    catch {
      /* already stopped */
    }
    oscillator = null;
  }

  if (gainNode) {
    try {
      gainNode.disconnect();
    }
    catch {
      /* ignore */
    }
    gainNode = null;
  }
}
